import {useEffect, useRef, useState} from "react";
import {Autocomplete, Paper, TextField} from "@mui/material";
import {ArrowUpward} from "@mui/icons-material";
import Modal from "../../../../../../material/modal/Modal";
import ModalConfig from "../../../../../../material/modal/ModalConfig";
import PopperWrapper from "../../../../../../material/popper/PopperWrapper";
import useOrderRepository from "../../../../../../repository/insurance/useOrderRepository";
import useStandardizedItemRepository from "../../../../../../repository/insurance/useStandardizedItemRepository";
import isNull from "../../../../../../mixin/isNull";
import useHelper from "../../../../../../use/useHelper";
import useEventDispatcher from "../../../../../../use/useEventDispatcher";
import parameter from "../../../../../../enum/parameter";
import CreateItem from "./CreateItem";

const removeAccent = str => str.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

const EXCLUDED_WORDS = ["le", "la", "et", "un", "en", "de", "une", "des", "les", "dans", "du", "a", "à"];

const filterOptions = (options, {inputValue}) => {
    if (inputValue.length === 0) return options;

    const needle = removeAccent(inputValue);

    const match = haystack =>
        needle.split(" ").every(word => EXCLUDED_WORDS.includes(word) || haystack.includes(word));

    return options.filter(si => match(removeAccent(si.getName())));
};

export default function ({orderId, onSelect, currentQuotationItems}) {
    const helper = useHelper();
    const eventDispatcher = useEventDispatcher();
    const orderRepository = useOrderRepository();
    const standardizedItemRepository = useStandardizedItemRepository();
    /** @type Order */
    const order = orderRepository.findOneById(orderId).select();
    /** @type StandardizedItem[] */
    const standardizedItems = standardizedItemRepository.findAllByWorkId(order?.getWorkId()).select();
    const [createCustom, setCreateCustom] = useState(null);
    const [reload, setReload] = useState(helper.uuid());
    const selectorRef = useRef();

    useEffect(() => {
        orderRepository.findOneById(orderId).exec();
    }, []);

    useEffect(() => {
        if (isNull(order)) return;

        standardizedItemRepository.findAllByWorkId(order.getWorkId()).exec();
    }, [JSON.stringify(order)]);

    useEffect(() => {
        if (isNull(standardizedItems) || standardizedItems.length === 0) return;

        setTimeout(() => eventDispatcher.launcher("select-standardized-item"), parameter.POPUP_TIME * 2);
    }, [JSON.stringify(standardizedItems)]);

    const handleCreateCustomSuccess = item => {
        onSelect(item);
        setCreateCustom(null);
    }

    if (isNull(standardizedItems)) return null;

    return (
        <>
            <Autocomplete
                key={standardizedItems.length + "-" + reload}
                freeSolo
                openOnFocus={true}
                size={"small"}
                PaperComponent={props => <Paper elevation={8} {...props}/>}
                options={standardizedItems.filter(si => !currentQuotationItems.some(cqi => cqi.name === si.getName()))}
                getOptionLabel={si => si._custom ? `Créer l'item "${si._inputValue}"` : si.getName()}
                getOptionKey={si => si._custom ? -1 : si.getId()}
                filterOptions={(options, params) => {
                    const filtered = filterOptions(options, params);

                    if (filtered.length > 0) return filtered;

                    const {inputValue} = params;

                    if (inputValue !== '') {
                        filtered.push({_custom: true, _inputValue: inputValue});
                    }

                    return filtered;
                }}
                clearOnBlur={true}
                renderInput={params => (
                    <TextField
                        {...params}
                        label="Sélectionnez ou créez un item"
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                "data-test": "insurance-input-standardized-item"
                            }
                        }}
                    />
                )}
                onChange={(e, si) => {
                    if (isNull(si)) return;

                    if (si._custom) {
                        setCreateCustom(si._inputValue);
                    } else {
                        onSelect({
                            name: si.getName(),
                            pricing: si.getPrice(),
                            quantity: 1,
                            standardized_item_id: si.getId()
                        });
                    }

                    setReload(helper.uuid());
                }}
                ref={selectorRef}
            />
            <PopperWrapper
                elementRef={selectorRef}
                placement={"bottom"}
                content={"Sélectionnez les fournitures nécessaires dans le catalogue"}
                triggeringEvent={"select-standardized-item"}
                autoCloseAfter={parameter.POPUP_TIME}
                displayCloseButton={false}
                contentColor={parameter.POPUP_COLOR}
                icon={<ArrowUpward color={parameter.POPUP_COLOR}/>}
            />
            {
                !isNull(createCustom)
                    ? (
                        <Modal
                            modalConfig={
                                new ModalConfig()
                                    .setOnClose(() => setCreateCustom(null))
                                    .setWidth("100%")
                                    .setHeight("80%")
                                    .setComponent(
                                        <CreateItem
                                            defaultName={createCustom}
                                            onSubmit={handleCreateCustomSuccess}
                                        />
                                    )
                            }
                        />
                    )
                    : null
            }
        </>
    );
}
